/**
 * SIL-1429 (slice 4 child 6 of SIL-1319) - the Express interest modal. Spec
 * section 5; handoff docs/exchange/design-handoff/ section 6.
 *
 * Shown to BOTH buyer tiers (verified and unverified) — the caller decides
 * visibility (canBuy + organizationType === 'business'); this component
 * assumes it is already allowed to render.
 *
 * `PUT /api/client/listings/:id/interest` is an upsert, so this modal always
 * opens prefilled from `GET .../interest` when a row already exists — there
 * is no separate "create" vs "edit" mode, only "has a saved interest or not".
 *
 * The region field (`buyerRegion`) is a 2026-09-30 scope addition the spec's
 * four fields (company category, use case, timeline, message) did not have —
 * see packages/shared/src/dto/listing-interest.ts and migration 973's header
 * for why it is the BUYER'S OWN stated region, never a projection of
 * `buyer_org.pref_regions` (a sourcing preference, not a location). It is
 * nullable end to end: the column, the DTO field and this form all leave it
 * optional, so a submission that omits it stays valid.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { track } from '@siliconx/shared/analytics';
import type {
  ListingInterestCompanyCategory,
  ListingInterestTimeline,
} from '@siliconx/shared/dto/listing-interest';
import { listingInterestCompanyCategorySchema, listingInterestTimelineSchema } from '@siliconx/shared/dto/listing-interest';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { Field } from '@siliconx/shared/ui/Field';
import { Select } from '@siliconx/shared/ui/Select';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { BUYER_VERIFICATION_ENTITY_TYPE_LABELS } from '@siliconx/shared/ui/buyer-listing/copy/buyerVerification';
import {
  EXPRESS_INTEREST_CANCEL,
  EXPRESS_INTEREST_FIELD_LABELS,
  EXPRESS_INTEREST_GENERIC_ERROR,
  EXPRESS_INTEREST_MODAL_SUBTITLE,
  EXPRESS_INTEREST_MODAL_TITLE,
  EXPRESS_INTEREST_REGION_PLACEHOLDER,
  EXPRESS_INTEREST_SELECT_PLACEHOLDER,
  EXPRESS_INTEREST_SUBMIT,
  EXPRESS_INTEREST_SUBMITTING,
  EXPRESS_INTEREST_SUCCESS_TOAST,
  EXPRESS_INTEREST_TIMELINE_LABELS,
  EXPRESS_INTEREST_UNKNOWN_REGION_ERROR,
} from '@siliconx/shared/ui/buyer-listing/copy/listingDetail';

interface FormState {
  companyCategory: ListingInterestCompanyCategory | '';
  useCase: string;
  timeline: ListingInterestTimeline | '';
  message: string;
  buyerRegion: string;
}

const EMPTY_FORM: FormState = {
  companyCategory: '',
  useCase: '',
  timeline: '',
  message: '',
  buyerRegion: '',
};

async function fetchOwnInterest(listingId: string) {
  const res = await api.api.client.listings[':id'].interest.$get({ param: { id: listingId } });
  if (res.status === 404) return null;
  if (!res.ok) {
    await throwIfNotOk(res as unknown as Response);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as unknown as {
    companyCategory: ListingInterestCompanyCategory | null;
    useCase: string | null;
    timeline: ListingInterestTimeline | null;
    message: string | null;
    buyerRegion: string | null;
  };
}

async function fetchRegions() {
  const res = await api.api.client.reference.$get();
  await throwIfNotOk(res as unknown as Response);
  const body = (await res.json()) as unknown as { regions: { id: string; name: string }[] };
  return body.regions;
}

/** True when `throwIfNotOk` rejected with the PUT handler's `unknown_region`
 * code (apps/api/src/routes/client/listings.ts) — a stale client-side
 * region list, not a generic failure. */
function isUnknownRegionError(err: unknown): boolean {
  const body = (err as { body?: { error?: { code?: string } } } | undefined)?.body;
  return body?.error?.code === 'unknown_region';
}

export function ExpressInterestModal({
  listingId,
  onClose,
}: {
  listingId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [regionError, setRegionError] = useState<string | null>(null);

  const existingQ = useQuery({
    queryKey: qk.listings.interest(listingId),
    queryFn: () => fetchOwnInterest(listingId),
  });
  const regionsQ = useQuery({ queryKey: qk.reference(), queryFn: fetchRegions });

  // Prefill once the saved interest (or its absence) is known. Re-runs only
  // when the fetch resolves — never overwrites what the buyer is mid-typing.
  useEffect(() => {
    if (!existingQ.isSuccess) return;
    const row = existingQ.data;
    setForm(
      row
        ? {
            companyCategory: row.companyCategory ?? '',
            useCase: row.useCase ?? '',
            timeline: row.timeline ?? '',
            message: row.message ?? '',
            buyerRegion: row.buyerRegion ?? '',
          }
        : EMPTY_FORM,
    );
  }, [existingQ.isSuccess, existingQ.data]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.listings[':id'].interest.$put({
        param: { id: listingId },
        json: {
          companyCategory: form.companyCategory === '' ? null : form.companyCategory,
          useCase: form.useCase.trim() === '' ? null : form.useCase.trim(),
          timeline: form.timeline === '' ? null : form.timeline,
          message: form.message.trim() === '' ? null : form.message.trim(),
          buyerRegion: form.buyerRegion === '' ? null : form.buyerRegion,
        },
      });
      await throwIfNotOk(res as unknown as Response);
    },
    onSuccess: () => {
      track('express_interest_submit', { item_id: listingId });
      toast.success(EXPRESS_INTEREST_SUCCESS_TOAST);
      void queryClient.invalidateQueries({ queryKey: qk.listings.interest(listingId) });
      onClose();
    },
    onError: (err: Error) => {
      if (isUnknownRegionError(err)) {
        track('form_error', { form: 'express_interest', reason: 'unknown_region', error_field: 'buyerRegion' });
        setRegionError(EXPRESS_INTEREST_UNKNOWN_REGION_ERROR);
        return;
      }
      track('form_error', { form: 'express_interest', reason: 'api' });
      toast.error(err.message ?? EXPRESS_INTEREST_GENERIC_ERROR);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="max-w-md w-full">
        <h2 className="text-base font-semibold text-foreground">{EXPRESS_INTEREST_MODAL_TITLE}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{EXPRESS_INTEREST_MODAL_SUBTITLE}</p>

        <div className="mt-4 space-y-4">
          <Field label={EXPRESS_INTEREST_FIELD_LABELS.companyCategory}>
            <Select
              value={form.companyCategory}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  companyCategory: e.target.value as ListingInterestCompanyCategory | '',
                }))
              }
            >
              <option value="">{EXPRESS_INTEREST_SELECT_PLACEHOLDER}</option>
              {listingInterestCompanyCategorySchema.options.map((option) => (
                <option key={option} value={option}>
                  {BUYER_VERIFICATION_ENTITY_TYPE_LABELS[option] ?? option}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={EXPRESS_INTEREST_FIELD_LABELS.useCase}>
            <textarea
              rows={2}
              value={form.useCase}
              onChange={(e) => setForm((f) => ({ ...f, useCase: e.target.value }))}
              className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              maxLength={500}
            />
          </Field>

          <Field label={EXPRESS_INTEREST_FIELD_LABELS.timeline}>
            <Select
              value={form.timeline}
              onChange={(e) =>
                setForm((f) => ({ ...f, timeline: e.target.value as ListingInterestTimeline | '' }))
              }
            >
              <option value="">{EXPRESS_INTEREST_SELECT_PLACEHOLDER}</option>
              {listingInterestTimelineSchema.options.map((option) => (
                <option key={option} value={option}>
                  {EXPRESS_INTEREST_TIMELINE_LABELS[option] ?? option}
                </option>
              ))}
            </Select>
          </Field>

          {/*
            Buyer-stated region, NOT the sourcing preference on the org
            profile (buyer_org.pref_regions) — see this file's header and
            migration 973. Nullable: no placeholder-is-required styling.
          */}
          <Field
            label={EXPRESS_INTEREST_FIELD_LABELS.buyerRegion}
            error={regionError ?? undefined}
          >
            <Select
              value={form.buyerRegion}
              onChange={(e) => {
                setForm((f) => ({ ...f, buyerRegion: e.target.value }));
                setRegionError(null);
              }}
            >
              <option value="">{EXPRESS_INTEREST_REGION_PLACEHOLDER}</option>
              {(regionsQ.data ?? []).map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={EXPRESS_INTEREST_FIELD_LABELS.message}>
            <textarea
              rows={3}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              maxLength={1000}
            />
          </Field>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitMutation.isPending}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {EXPRESS_INTEREST_CANCEL}
          </button>
          <Button
            variant="primary"
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? EXPRESS_INTEREST_SUBMITTING : EXPRESS_INTEREST_SUBMIT}
          </Button>
        </div>
      </Card>
    </div>
  );
}
